import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// Cycles the /trial-started/ "check your email" line through its translations, one at a
// time, the way the iPhone's setup greeting does. Every `[data-cycle-set]` inside the root
// steps together — today the status pill and the line under it — so both always show the
// same language.
//
// Unlike word-cycle.js this never touches text. Every translation is already in the
// markup with its own lang, stacked in one grid cell, and this only moves classes — the
// blur-in and blur-out live in _trial-started.scss. Swapping textContent would strip lang
// off every line but the first.
//
// No aria-live, for the same reason as word-cycle.js: it is one message stated six ways,
// and the hidden lines are still in the accessibility tree, so a screen reader already
// reads all of them.

// The design's default.
const INTERVAL_MS = 3000;

// Has to outlast `trial-out` (700ms) in _trial-started.scss, or the outgoing line loses
// its class mid-fade and snaps back to plain opacity: 0.
const LEAVE_MS = 750;

class MessageCycle {
    constructor(elem) {
        this.elem = elem;
        this.sets = [...elem.querySelectorAll("[data-cycle-set]")].map((set) => [...set.children]);
        this.count = Math.min(...this.sets.map((lines) => lines.length));
        this.index = 0;
        this.timer = null;

        // The sets step by a shared index, so a set one translation short would show a
        // stale language beside the others. Cycle only as far as every set reaches.
        if (!this.sets.length || this.count < 2) return;

        if (!prefersReducedMotion()) this.schedule();

        // Both directions, as in word-cycle.js: the preference behaves as a live toggle.
        onReducedMotionChange(this.handleMotionPreference);
    }

    handleMotionPreference = () => {
        if (prefersReducedMotion()) {
            clearTimeout(this.timer);
            this.timer = null;
        } else {
            this.schedule();
        }
    };

    schedule() {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.advance, INTERVAL_MS);
    }

    advance = () => {
        const next = (this.index + 1) % this.count;

        this.elem.classList.remove("is-static");

        for (const lines of this.sets) {
            const leaving = lines[this.index];

            leaving.classList.remove("is-active");
            leaving.classList.add("is-leaving");
            setTimeout(() => leaving.classList.remove("is-leaving"), LEAVE_MS);

            lines[next].classList.remove("is-leaving");
            lines[next].classList.add("is-active");
        }

        this.index = next;
        this.schedule();
    };
}

document.querySelectorAll("[data-message-cycle]").forEach((elem) => new MessageCycle(elem));
